import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Check, MapPin } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';

import { useGetUserContextQuery } from '@/entities/layout';
import { createIdentifiableSlug } from '@/shared/lib/utils/slug-utils';
import { useProjectSiteParams } from '@/shared/lib';
import { baseApi } from '@/shared/api/base';
import { API_TAGS } from '@/shared/api/tags';
import type { AppDispatch } from '@/app/types/store';

export const ProjectSiteSelector = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { data: userContext } = useGetUserContextQuery();
  const { projectId, siteId } = useProjectSiteParams();

  const handleSiteSelect = (selectedProjectId: number, selectedSiteId: number) => {
    if (selectedProjectId === projectId && selectedSiteId === siteId) {
      setOpen(false);
      return;
    }

    const project = userContext?.projects.find((p) => p.id === selectedProjectId);
    const site = project?.sites.find((s) => s.id === selectedSiteId);

    if (project && site) {
      dispatch(
        baseApi.util.invalidateTags([
          { type: API_TAGS.LAYOUT, id: `${selectedProjectId}-${selectedSiteId}` },
          API_TAGS.DASHBOARD,
          API_TAGS.TRANSPORT,
          API_TAGS.STORAGE,
          API_TAGS.SERVICE_USERS,
          API_TAGS.MAPPING,
          API_TAGS.OPTIONS
        ])
      );

      const projectSlug = createIdentifiableSlug(project.name, project.id);
      const siteSlug = createIdentifiableSlug(site.name, site.id);

      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const pathParts = currentPath.split('/');
      const restPath = pathParts.slice(3).join('/') || '';

      navigate(`/${projectSlug}/${siteSlug}/${restPath}${currentSearch}`, { replace: true });
      setOpen(false);
    }
  };

  if (!userContext) return null;

  const selectedProject = userContext.projects.find((p) => p.id === projectId);
  const selectedSite = selectedProject?.sites.find((s) => s.id === siteId);
  // Highlight the current site when the list opens so cmdk scrolls it into
  // view; must match the CommandItem value below exactly.
  const selectedValue = selectedProject && selectedSite ? `${selectedProject.name} ${selectedSite.name}` : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="start">
        <Command className="rounded-lg" defaultValue={selectedValue}>
          <CommandInput placeholder="Search projects and sites..." />
          <CommandList>
            <CommandEmpty>No projects or sites found.</CommandEmpty>

            {userContext.projects.map((project, index) => (
              <div key={project.id}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={project.name}>
                  {project.sites.map((site) => {
                    const isSelected = projectId === project.id && siteId === site.id;
                    return (
                      <CommandItem
                        key={`${project.id}-${site.id}`}
                        value={`${project.name} ${site.name}`}
                        onSelect={() => handleSiteSelect(project.id, site.id)}
                        className="cursor-pointer">
                        <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span className="flex-1">{site.name}</span>
                        {isSelected && <Check className="ml-2 h-4 w-4 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
